import { useEffect, useLayoutEffect, useMemo, useCallback, useRef } from "react";
import { getAllWidgets } from "../../../utils/formsHelper";
import { Flex, Form, Input as AntdInput } from "antd"
import { QuestionCircleOutlined } from "@ant-design/icons"
import { useParams } from "react-router-dom"
import { getActiveSetValue, getConditionalRevertValue, shouldRevertConditionalValue } from "../../../utils/formsHelper";
import { getPreviousDataByValue, getPublicPreviousDataByValue } from "../../../services/requestServices";
import { safeDayjs } from "../../../utils/safeDate.js";
import dayjs from "dayjs"
import { formatWithThousandSeparators, mapCursorAfterFormat, stripThousandSeparators, toNumericValue } from "../../../utils/numberFormatter.js"

const MS_PER_DAY = 86400000

const escapeRegex = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

const NumberInput = (props) => {
    const { spaceId, requestId } = useParams()
    const { label, placeholder, tooltip, helpText, mandatory, readOnly, id, unit, formulaHtml, allowedDecimalPoint, allowNegative, previewMode, previewmode, form, setValueData, calculationField, minValue, maxValue, enablePreviousDataFetch, requestDetail, isPublicForm = false } = props
    const isPreview = previewMode || previewmode;
    const isEditing = !!(requestDetail && Object.keys(requestDetail).length > 0);
    // if (label?.includes("ACTUAL PAID")) {

    // }
    // When decimals are not allowed, treat min/max as digit-length constraints
    const lengthMode = !props.allowDecimal

    // Build form rules dynamically (required + optional range/length validation)
    let rules = []
    if (mandatory) {
        rules.push({ required: true, whitespace: true })
    }
    if (minValue !== undefined || maxValue !== undefined) {
        rules.push({
            validator: (_, value) => {
                if (value === undefined || value === null || value === "") return Promise.resolve()
                if (lengthMode) {
                    const digits = String(value).replace(/[^0-9]/g, '').length
                    if (minValue !== undefined && digits < Number(minValue)) {
                        return Promise.reject(new Error(`Enter at least ${minValue} digits`))
                    }
                    if (maxValue !== undefined && digits > Number(maxValue)) {
                        return Promise.reject(new Error(`Enter at most ${maxValue} digits`))
                    }
                } else {
                    const num = Number(stripThousandSeparators(value))
                    if (!Number.isFinite(num)) return Promise.reject(new Error("Enter a valid number"))
                    if (minValue !== undefined && num < Number(minValue)) {
                        return Promise.reject(new Error(`Value must be ≥ ${minValue}`))
                    }
                    if (maxValue !== undefined && num > Number(maxValue)) {
                        return Promise.reject(new Error(`Value must be ≤ ${maxValue}`))
                    }
                }
                return Promise.resolve()
            }
        })
    }
    const disabled = previewMode && readOnly ? true : false
    const help = tooltip ? { title: helpText, icon: <QuestionCircleOutlined /> }
        : undefined;
    let numberPrecision = (allowedDecimalPoint !== undefined && allowedDecimalPoint !== "" && allowedDecimalPoint !== 0) ? Number(allowedDecimalPoint) : undefined

    let displayValue = 0

    // Pure evaluation of the formula (no side effects)
    const evaluateFormula = useCallback(() => {
        if (!props.computedNumber && !props.isDateComputation) return null

        const getCurrentNumericFieldValue = (fieldId) => {
            const currentValue = form.getFieldValue(fieldId)
            if (currentValue !== undefined) return toNumericValue(currentValue)
            return toNumericValue(props.formValues?.[fieldId])
        }

        let hasMissingDate = false

        const getCurrentDateAsDays = (fieldId) => {
            const currentValue = form.getFieldValue(fieldId)
            const rawValue = currentValue !== undefined && currentValue !== null
                ? currentValue
                : props.formValues?.[fieldId]
            if (rawValue === undefined || rawValue === null || rawValue === "") {
                hasMissingDate = true
                return 0
            }
            const date = safeDayjs(rawValue)
            if (!date.isValid()) {
                hasMissingDate = true
                return 0
            }
            return Math.floor(date.valueOf() / MS_PER_DAY)
        }

        const targetFormType = props.isDateComputation ? "dateForm" : "numberForm"
        const resolveValue = props.isDateComputation ? getCurrentDateAsDays : getCurrentNumericFieldValue

        // Prefer raw formula first (label based), then fallback to formulaHtml (id based)
        let expression = null
        if (props.formula) {
            expression = String(props.formula)
            const allWidgets = props.formElements ? getAllWidgets(props.formElements) : []
            const matchedWidgets = allWidgets
                .filter((widget) => widget?.formType === targetFormType && widget?.label)
                .sort((left, right) => right.label.length - left.label.length)

            matchedWidgets.forEach((widget) => {
                const token = '@' + widget.label
                if (!expression.includes(token)) return
                const pattern = new RegExp(`@${escapeRegex(widget.label)}`, "g")
                expression = expression.replace(pattern, String(resolveValue(widget.id)))
            })
        }

        if (formulaHtml) {
            const parser = new DOMParser();
            let parsedDocument = parser.parseFromString(formulaHtml, 'text/html');
            let allWidgets = parsedDocument.querySelectorAll(`.widget-inline-block`)
            for (const elm of allWidgets) {
                const widget = elm.getAttribute("data-value")
                elm.replaceWith(String(resolveValue(widget)))
            }
            if (!expression || expression.trim() === "") {
                expression = (parsedDocument.body?.textContent || '').trim()
            }
        }

        if (props.isDateComputation && hasMissingDate) return 0

        if (!expression) return null

        // Replace unresolved @tokens with 0 to keep formulas stable
        let str = expression.replace(/@[A-Za-z0-9_\-./\s]+/g, '0').replace(/@/g, '')

        // Guard: only evaluate safe math expressions; otherwise return the current value without error noise
        const compact = str.replace(/\s+/g, '')
        const prevNumber = getCurrentNumericFieldValue(id)
        if (!compact) return prevNumber
        // Contains only digits, operators, parentheses, dot
        if (/[^0-9+\-*/().]/.test(compact)) return prevNumber
        // Avoid evaluating when expression ends with an operator or opening paren
        if (/[+\-*/.(]$/.test(compact)) return prevNumber

        try {
            // eslint-disable-next-line no-new-func
            let result = new Function('return ' + str)();
            let calculatedValue = Number(result)
            // Guard against invalid results (NaN / Infinity from divide-by-zero, etc.)
            if (!Number.isFinite(calculatedValue)) return prevNumber
            if (numberPrecision !== undefined && numberPrecision >= 0) {
                calculatedValue = Number(Number(calculatedValue).toFixed(numberPrecision))
            }
            return calculatedValue
        } catch {
            return prevNumber
        }
    }, [props.computedNumber, props.isDateComputation, props.formula, props.formElements, formulaHtml, form, props.formValues, numberPrecision, id, label])

    const computedValue = useMemo(() => evaluateFormula(), [evaluateFormula])

    // Keep form store in sync with computed value. This runs in the layout phase so
    // the store write (and the re-render it triggers in fields computed *from* this
    // one) is flushed before the browser paints — a plain effect lets the stale
    // value hit the screen for a frame on every keystroke in a source field.
    useLayoutEffect(() => {
        if (!props.computedNumber && !props.isDateComputation) return
        if (computedValue === null || computedValue === undefined) return
        const prev = form.getFieldValue(id)
        const next = String(computedValue)
        if (prev !== next) {
            form.setFieldValue(id, next)
        }
    }, [computedValue, form, id, props.computedNumber, props.isDateComputation])

    // Compute table column sum (e.g. "Material Total") — derived during render
    // but synced to form in an effect to avoid infinite update loops
    const tableColumnSum = useMemo(() => {
        if (!props.isTableColumnComputation) return null
        if (!calculationField?.tableId || !calculationField?.columnId) return null
        const tableValues = props.formValues?.[calculationField.tableId]
        if (!Array.isArray(tableValues) || tableValues.length === 0) return 0
        let sumValue = 0
        tableValues.forEach(value => {
            sumValue += toNumericValue(value[calculationField.columnId])
        })
        return sumValue
    }, [props.isTableColumnComputation, calculationField, props.formValues])

    useLayoutEffect(() => {
        if (tableColumnSum === null) return
        const prev = form.getFieldValue(id)
        if (String(prev) !== String(tableColumnSum)) {
            form.setFieldValue(id, tableColumnSum)
        }
    }, [tableColumnSum, form, id])

    if (props.computedNumber || props.isDateComputation) {
        displayValue = Number.isFinite(computedValue) ? computedValue : 0
    } else if (props.isTableColumnComputation) {
        displayValue = tableColumnSum ?? 0
    } else {
        // For normal numbers and computed numbers, rely on current form value for display
        displayValue = form?.getFieldValue(id) ?? props.formValues?.[id] ?? undefined
    }

    // A number input is scalar; if a non-primitive value slipped into the store
    // (e.g. an object copied in via a set-value condition), unwrap it instead of
    // rendering "[object Object]".
    if (displayValue !== null && typeof displayValue === "object") {
        displayValue = displayValue.value ?? ""
    }

    // Form.Item injects the *store* value into its child and it wins over any `value`
    // prop we pass (rc-field-form merges `{...childProps, ...valueProps}`). For a
    // computed field the store is only caught up by the effect above, so the input
    // would paint the previous result first and correct itself afterwards — the
    // flicker. `getValueProps` overrides that injection with the value derived in
    // this render, so what is painted is always the current one.
    const isComputedField = !!(props.computedNumber || props.isDateComputation || props.isTableColumnComputation)
    // Form store keeps the raw number; getValueProps is the only display path so
    // commas never land in formulas, conditions, or the submit payload.
    const numberValueProps = useCallback(
        (storeValue) => {
            const source = isComputedField
                ? (displayValue === undefined || displayValue === null ? "" : displayValue)
                : storeValue
            if (source === undefined || source === null || source === "") return { value: "" }
            return { value: formatWithThousandSeparators(source) }
        },
        [displayValue, isComputedField]
    )

    let activeSetValue = getActiveSetValue(setValueData)

    // Track the condition value we've already applied so an editable field is
    // prefilled only once. Re-applying on every render would clobber the user's
    // own edits; we only push the value again when the condition value changes.
    const appliedSetValueRef = useRef(undefined)

    useEffect(() => {
        const setVal = activeSetValue?.value
        const hasSetValue = setVal !== undefined && setVal !== null && setVal !== ""
        if (hasSetValue) {
            if (appliedSetValueRef.current !== setVal) {
                form?.setFieldValue(id, setVal)
                appliedSetValueRef.current = setVal
            }
        } else if (shouldRevertConditionalValue(activeSetValue, { disabled, currentValue: form?.getFieldValue(id), appliedValue: appliedSetValueRef.current })) {
            form?.setFieldValue(id, getConditionalRevertValue(id, props.defaultFormValues))
            appliedSetValueRef.current = undefined
        } else {
            // No active set value — reset so the same value can be re-applied
            // if the condition re-triggers later.
            appliedSetValueRef.current = undefined
        }
    }, [activeSetValue, disabled, form, id])

    // Blocked keystrokes (decimal / minus when the field disallows them) otherwise
    // look like a dead keyboard, so surface a transient message on the field itself.
    const errorTimerRef = useRef(null)
    const inputRef = useRef(null)
    const pendingCursorRef = useRef(null)

    const showInputError = useCallback((message) => {
        if (!form) return
        form.setFields([{ name: id, errors: [message] }])
        if (errorTimerRef.current) clearTimeout(errorTimerRef.current)
        errorTimerRef.current = setTimeout(() => {
            errorTimerRef.current = null
            form.setFields([{ name: id, errors: [] }])
        }, 2500)
    }, [form, id])

    // Only clear when we are the ones who set the message, so a genuine rule
    // error (required / min / max) is never wiped by a keystroke.
    const clearInputError = useCallback(() => {
        if (!errorTimerRef.current) return
        clearTimeout(errorTimerRef.current)
        errorTimerRef.current = null
        form?.setFields([{ name: id, errors: [] }])
    }, [form, id])

    useEffect(() => () => {
        if (errorTimerRef.current) clearTimeout(errorTimerRef.current)
    }, [])

    const handleBlur = (event) => {
        if (!event.target.value) {
            return null
        }
        const rawValue = stripThousandSeparators(event.target.value)
        let finalFormatted = rawValue
        if (finalFormatted.includes(".") && (numberPrecision !== undefined && numberPrecision >= 0)) {
            let formatNumber = String(displayValue || "").split(".")
            let decimalNumber = formatNumber[1]?.substring(0, numberPrecision) || 0
            finalFormatted = formatNumber[0] + "." + decimalNumber
        }
        if (finalFormatted.endsWith(".")) {
            finalFormatted = finalFormatted.slice(0, -1)
        }
        // Optional soft clamp for min/max on blur (keeps user-friendly behavior without forcing change during typing)
        if (!lengthMode) {
            const num = Number(finalFormatted)
            if (!Number.isNaN(num)) {
                let next = num
                if (minValue !== undefined && num < Number(minValue)) next = Number(minValue)
                if (maxValue !== undefined && next > Number(maxValue)) next = Number(maxValue)
                if (next !== num) {
                    form?.setFieldValue(id, String(next))
                }
            }
        }
        if (enablePreviousDataFetch && isPreview && !isEditing) {
            fetchPreviousData(rawValue);
        }
        // form.setFieldValue(id, finalFormatted)
    }

    const fetchPreviousData = async (value) => {
        const response = isPublicForm
            ? await getPublicPreviousDataByValue(id, value, props.requestId, props.spaceId, props.shareToken)
            : await getPreviousDataByValue(id, value, spaceId, requestId);
        const fetchedValues = response?.data?.value;
        if (response.status === 200 && fetchedValues && typeof fetchedValues === "object") {
            const fieldsToSet = { ...fetchedValues };
            delete fieldsToSet[id];
            if (Object.keys(fieldsToSet).length > 0) {
                form?.setFieldsValue(fieldsToSet);
            }
        }
    };

    const sanitize = (raw) => {
        if (raw === undefined || raw === null) return ''
        let s = String(raw)
        // Remove commas and surrounding whitespace
        s = s.replace(/,/g, '').trim()
        // Build allowed pattern depending on props
        let pattern = null
        if (props.allowDecimal) {
            pattern = allowNegative ? /-?[0-9]*\.?[0-9]*/g : /[0-9]*\.?[0-9]*/g
        } else {
            pattern = allowNegative ? /-?[0-9]*/g : /[0-9]*/g
        }
        const matches = s.match(pattern)
        const next = (matches && matches[0]) || ''
        // Enforce decimal precision
        if (props.allowDecimal && numberPrecision !== undefined && next.includes('.')) {
            const parts = next.split('.')
            parts[1] = (parts[1] || '').substring(0, numberPrecision)
            return parts[0] + (parts[1] !== undefined ? '.' + parts[1] : '')
        }
        return next
    }

    const handleKeyPress = (event) => {
        const key = event.key
        // Allow: backspace, delete, tab, escape, enter, arrow keys
        if (["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(key) ||
            // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            (event.ctrlKey && ["a", "c", "v", "x"].includes(key.toLowerCase()))) {
            return
        }

        const { value, selectionStart, selectionEnd } = event.target
        const minusKeys = ["-", "Subtract", "	6"]

        // Handle decimal point
        if (key === '.') {
            if (!props.allowDecimal) {
                showInputError("Decimal values are not allowed")
                return event.preventDefault()
            }
            if (value.includes('.')) return event.preventDefault()
            // Valid decimal point — allow it (otherwise the digit-only guard below blocks it)
            return
        }

        // Handle negative sign
        if (minusKeys.includes(key)) {
            if (!allowNegative) {
                showInputError("Negative values are not allowed")
                return event.preventDefault()
            }
            const hasMinus = value.includes('-')
            const atStart = selectionStart === 0
            if (hasMinus || !atStart) return event.preventDefault()
            return
        }

        // Ensure digit — block any character not allowed by the numeric rules
        if (!/[0-9]/.test(key)) {
            return event.preventDefault()
        }

        // Compose next value to be typed
        const nextValue = stripThousandSeparators(value.slice(0, selectionStart) + key + value.slice(selectionEnd))

        // Enforce decimal precision while typing
        if (!lengthMode && props.allowDecimal && numberPrecision !== undefined && nextValue.includes('.')) {
            const dec = nextValue.split('.')[1]
            if (dec && dec.length > numberPrecision) {
                return event.preventDefault()
            }
        }

        // Block typing beyond limits
        if (maxValue !== undefined) {
            if (lengthMode) {
                const digits = nextValue.replace(/[^0-9]/g, '').length
                if (digits > Number(maxValue)) return event.preventDefault()
            } else {
                const num = Number(nextValue)
                if (!Number.isNaN(num) && Number.isFinite(num) && num > Number(maxValue)) {
                    return event.preventDefault()
                }
            }
        }
    }

    const handlePaste = (event) => {
        // Get pasted data
        const paste = stripThousandSeparators((event.clipboardData || window.clipboardData).getData('text'));

        // Check if the pasted data is a valid number
        const isValidNumber = props.allowDecimal
            ? (allowNegative ? /^-?\d*\.?\d*$/.test(paste) : /^\d*\.?\d*$/.test(paste))
            : (allowNegative ? /^-?\d*$/.test(paste) : /^\d*$/.test(paste));

        if (!isValidNumber) {
            if (!props.allowDecimal && paste.includes('.')) {
                showInputError("Decimal values are not allowed")
            } else if (!allowNegative && paste.includes('-')) {
                showInputError("Negative values are not allowed")
            } else {
                showInputError("Enter a valid number")
            }
            event.preventDefault();
        } else {
            // Additional check for decimal precision
            if (props.allowDecimal && paste.includes('.') && numberPrecision !== undefined) {
                const decimalPart = paste.split('.')[1];
                if (decimalPart && decimalPart.length > numberPrecision) {
                    event.preventDefault();
                }
            }
            // Block paste if it would exceed max/min according to mode
            if (lengthMode) {
                const digits = paste.replace(/[^0-9]/g, '').length
                if (maxValue !== undefined && digits > Number(maxValue)) {
                    event.preventDefault()
                }
                // If less than min digits, allow paste; validation will show message
            } else {
                const num = Number(paste)
                if (!Number.isNaN(num)) {
                    if (maxValue !== undefined && num > Number(maxValue)) {
                        event.preventDefault()
                    }
                    if (minValue !== undefined && num < Number(minValue)) {
                        event.preventDefault()
                    }
                }
            }
        }
    }

    const getValueFromEvent = (event) => sanitize(event?.target?.value)

    const handleChange = (event) => {
        clearInputError()
        const oldFormatted = event.target.value
        const cursor = event.target.selectionStart
        const nextFormatted = formatWithThousandSeparators(sanitize(oldFormatted))
        pendingCursorRef.current = mapCursorAfterFormat(oldFormatted, nextFormatted, cursor)
    }

    useLayoutEffect(() => {
        if (pendingCursorRef.current === null) return
        const el = inputRef.current?.input || inputRef.current
        if (el && typeof el.setSelectionRange === 'function') {
            el.setSelectionRange(pendingCursorRef.current, pendingCursorRef.current)
        }
        pendingCursorRef.current = null
    })

    return (
        <div className="form-element">
            <Flex vertical>
                <Form.Item name={id} label={label} tooltip={help} rules={rules} getValueProps={numberValueProps} getValueFromEvent={getValueFromEvent} >
                    <AntdInput
                        ref={inputRef}
                        disabled={disabled}
                        placeholder={placeholder}
                        value={displayValue || ""}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyPress}
                        onPaste={handlePaste}
                        onChange={handleChange}
                        style={{ width: "100%" }}
                        suffix={unit}
                        size="large"
                        inputMode={props.allowDecimal ? "decimal" : "numeric"}
                    />
                    {/* <InputNumber parser={(value) => value.replace(/[^0-9.]/g, '')} stringMode={false} step={1} precision={allowedDecimalPoint} min={0} placeholder={placeholder} style={{ width: "100%" }} disabled={disabled} size="large" suffix={unit} /> */}
                </Form.Item>
            </Flex>
        </div>
    )
}

export default NumberInput
